<template>
  <base-module
    :definition="resistanceDefinitions"
    :connected="resistanceWheelStatus.connected"
    :connecting="resistanceWheelStatus.connecting"
    :content-active="
      resistanceWheelStatus.connected && !resistanceWheelStatus.connecting
    "
  >
    <template #title> {{ t('modules.resistance.index') }} </template>
    <template #config>
      <div
        class="resistance-module__settings"
        @click="changeDevice = true"
      >
        <BfIcon
          icon="/icons/01-Interface Essential/12-Settings/cog.svg"
          :color="Colors.Light"
        />
      </div>
    </template>

    <template #middle>
      <n-slider
        v-if="mode === 'powerTarget'"
        v-model:value="powerTargetValue"
        :step="RESISTANCE_POWER_TARGET_STEP"
        :min="RESISTANCE_POWER_TARGET_MIN"
        :max="RESISTANCE_POWER_TARGET_MAX"
      />

      <n-slider
        v-if="mode === 'resistanceLevel'"
        v-model:value="resistanceLevelValue"
        :step="RESISTANCE_LEVEL_STEP"
        :min="RESISTANCE_LEVEL_MIN"
        :max="RESISTANCE_LEVEL_MAX"
      />

      <n-alert
        v-if="mode === 'external'"
        :title="t('modules.resistance.external.option')"
        :bordered="false"
      >
        <template #icon>
          <Icon
            icon="mdi:information-outline"
            :style="{ fontSize: '24px' }"
          />
        </template>
        <p>
          {{ t('modules.resistance.external.noControls') }}
        </p>
      </n-alert>
    </template>

    <template #bottom>
      <n-form
        ref="formRef"
        label-placement="left"
      >
        <n-form-item
          v-if="mode === 'powerTarget'"
          class="resistance-module__input"
          :label="t('modules.resistance.powerTarget')"
        >
          <n-input-group>
            <n-input-number
              v-model:value="powerTargetValue"
              :step="RESISTANCE_POWER_TARGET_STEP"
              :min="RESISTANCE_POWER_TARGET_MIN"
              :max="RESISTANCE_POWER_TARGET_MAX"
              button-placement="both"
            />

            <n-input-group-label>{{ powerTarget.unit }}</n-input-group-label>
          </n-input-group>
        </n-form-item>

        <n-form-item
          v-if="mode === 'resistanceLevel'"
          class="resistance-module__input"
          :label="t('modules.resistance.resistanceLevel')"
        >
          <n-input-group>
            <n-input-number
              v-model:value="resistanceLevelValue"
              :step="RESISTANCE_LEVEL_STEP"
              :min="RESISTANCE_LEVEL_MIN"
              :max="RESISTANCE_LEVEL_MAX"
              button-placement="both"
            />

            <n-input-group-label>{{
              resistanceLevel.unit
            }}</n-input-group-label>
          </n-input-group>
        </n-form-item>

        <n-form-item>
          <n-select
            v-model:value="mode"
            :options="modeOptions"
          ></n-select>
        </n-form-item>
      </n-form>

      <div
        v-if="changeDevice"
        class="heartrate-module__select"
      >
        <PairDevices
          :type="deviceType.resistanceWheel"
          @close="changeDevice = false"
        />
      </div>

      <ResistanceExternal
        v-if="externalModal"
        :text="modalText"
        @close="closeModal"
      />
    </template>
  </base-module>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { deviceType } from '@/types/device'
import { useDebounceFn } from '@vueuse/core'
import { Icon } from '@iconify/vue'
import BaseModule from './BaseModule.vue'
import {
  NSlider,
  NInputNumber,
  NInputGroup,
  NInputGroupLabel,
  NAlert,
  NForm,
  NFormItem,
  NSelect,
} from 'naive-ui'
import { BfIcon, Colors } from '@finetic/bikefitting-ui'
import {
  RESISTANCE_POWER_TARGET_MIN,
  RESISTANCE_POWER_TARGET_MAX,
  RESISTANCE_POWER_TARGET_STEP,
  RESISTANCE_LEVEL_MIN,
  RESISTANCE_LEVEL_MAX,
  RESISTANCE_LEVEL_STEP,
} from '@/outputVariables'
import { ResistanceUnitMode } from '@/types/resistance'
import PairDevices from '@/components/controls/PairDevices.vue'
import ResistanceExternal from '@/components/controls/ResistanceExternal.vue'

//Store
import { useMqttStore } from '@/store/mqttStore'
import { useResistanceStore } from '@/store/resistanceStore'
import { useDeviceStore } from '@/store/deviceStore'
import { storeToRefs } from 'pinia'
import { ResistanceModalText } from '@/types/resistance'

//Mqtt
import {
  resistanceDefinitions,
  powerTarget,
  resistanceLevel,
} from '@/mqttDefinitions'
import { MqttMessage } from '@/mqttDefinitions'
import { sleep } from '@/utils/utils'

const { t } = useI18n()

const mqttStore = useMqttStore()
const resistanceStore = useResistanceStore()
const deviceStore = useDeviceStore()
const { doPublish } = mqttStore
const { client } = storeToRefs(mqttStore)
const { mode } = storeToRefs(resistanceStore)
const { resistanceWheelStatus } = storeToRefs(deviceStore)
const { doDevicesRefresh } = useDeviceStore()

const changeDevice = ref(false)
const externalModal = ref(false)
const modalText = ref(ResistanceModalText.ENTER)

//Data init
const currentPowerTarget = ref(RESISTANCE_POWER_TARGET_MIN)
const currentResistanceLevel = ref(RESISTANCE_LEVEL_MIN)

const modeOptions = ref([
  {
    label: t('modules.resistance.powerTarget'),
    value: ResistanceUnitMode.POWER_TARGET,
  },
  {
    label: t('modules.resistance.resistanceLevel'),
    value: ResistanceUnitMode.RESISTANCE_LEVEL,
  },
  {
    label: t('modules.resistance.external.option'),
    value: ResistanceUnitMode.EXTERNAL,
  },
])

const setPowerTargetValue = useDebounceFn(() => {
  doPublish(
    {
      topic: powerTarget.topic,
      payload: currentPowerTarget.value,
    } as MqttMessage,
    powerTarget,
    client.value,
  )
}, 200)

const setResistanceLevelValue = useDebounceFn(() => {
  doPublish(
    {
      topic: resistanceLevel.topic,
      payload: currentResistanceLevel.value,
    } as MqttMessage,
    resistanceLevel,
    client.value,
  )
}, 200)

const powerTargetValue = computed({
  get() {
    return currentPowerTarget.value
  },
  set(newValue) {
    currentPowerTarget.value = newValue
    setPowerTargetValue()
  },
})

const resistanceLevelValue = computed({
  get() {
    return currentResistanceLevel.value
  },
  set(newValue) {
    currentResistanceLevel.value = newValue
    setResistanceLevelValue()
  },
})

const closeModal = () => {
  externalModal.value = false
  setMode()
}

const setMode = async () => {
  if (mode.value === ResistanceUnitMode.POWER_TARGET) {
    doDevicesRefresh()
    await sleep(3000)
    powerTargetValue.value = 0
  } else if (mode.value === ResistanceUnitMode.RESISTANCE_LEVEL) {
    doDevicesRefresh()
    await sleep(3000)
    resistanceLevelValue.value = 0
  }
}

watch(mode, (newValue, oldValue) => {
  switch (newValue) {
    case ResistanceUnitMode.POWER_TARGET:
      if (oldValue === ResistanceUnitMode.EXTERNAL) {
        externalModal.value = true
        modalText.value = ResistanceModalText.LEAVE
      } else {
        setMode()
      }
      break

    case ResistanceUnitMode.RESISTANCE_LEVEL:
      if (oldValue === ResistanceUnitMode.EXTERNAL) {
        externalModal.value = true
        modalText.value = ResistanceModalText.LEAVE
      } else {
        setMode()
      }
      break

    case ResistanceUnitMode.EXTERNAL:
      externalModal.value = true
      modalText.value = ResistanceModalText.ENTER
      break
  }
})
</script>

<style lang="scss" scoped>
:deep(.n-input .n-input__input-el) {
  text-align: center;
}

.resistance-module {
  &__settings {
    opacity: 0.6;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }

  &__input {
    margin-bottom: 24px;
  }
}
</style>
